import { Bell, Check, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { useNotifications } from '../../contexts/NotificationContext'

export function NotificationPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()

  const buttonStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#6b7280'
  }

  const badgeStyle = {
    position: 'absolute' as const,
    top: '-4px',
    right: '-4px',
    width: '18px',
    height: '18px',
    background: '#ef4444',
    color: '#fff',
    fontSize: '0.625rem',
    fontWeight: '600',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  }

  const dropdownStyle = {
    position: 'absolute' as const,
    right: 0,
    top: '100%',
    marginTop: '0.5rem',
    width: '320px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    zIndex: 50,
    overflow: 'hidden',
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? 'visible' as const : 'hidden' as const,
    transform: isOpen ? 'translateY(0)' : 'translateY(-10px)',
    transition: 'all 0.2s ease'
  }

  const dropdownHeaderStyle = {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb'
  }

  const dropdownContentStyle = {
    padding: '1rem',
    maxHeight: '300px',
    overflowY: 'auto' as const
  }

  const notificationItemStyle = {
    padding: '0.75rem',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    background: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }

  const notificationTitleStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '0.25rem'
  }

  const notificationTextStyle = {
    fontSize: '0.75rem',
    color: '#6b7280',
    lineHeight: '1.4'
  }

  const notificationTimeStyle = {
    fontSize: '0.625rem',
    color: '#9ca3af',
    marginTop: '0.5rem'
  }

  const visibleNotifications = notifications.slice(0, 10)

  const formatTime = (timestamp: number) => {
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return 'agora'
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={buttonStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#e5e7eb'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#f3f4f6'
        }}
      >
        <Bell style={{ width: '18px', height: '18px' }} />
        {unreadCount > 0 && (
          <span style={badgeStyle}>{unreadCount}</span>
        )}
      </button>

      <div style={dropdownStyle}>
        <div style={dropdownHeaderStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
            Notificações
          </h3>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            {unreadCount} não lidas
          </p>
          {notifications.length > 0 && (
            <button
              onClick={markAllAsRead}
              style={{
                marginTop: '0.5rem',
                fontSize: '0.75rem',
                color: '#2563eb',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
        <div style={dropdownContentStyle}>
          {visibleNotifications.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Nenhuma notificação por enquanto.
            </div>
          ) : (
          visibleNotifications.map(notification => (
            <div
              key={notification.id}
              style={notificationItemStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6'
                e.currentTarget.style.transform = 'translateX(4px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f9fafb'
                e.currentTarget.style.transform = 'translateX(0)'
              }}
              onClick={() => markAsRead(notification.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={notificationTitleStyle}>{notification.title}</div>
                {!notification.read && <span style={{ fontSize: '0.625rem', color: '#ef4444' }}>novo</span>}
              </div>
              <div style={notificationTextStyle}>{notification.message}</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <div style={notificationTimeStyle}>{formatTime(notification.timestamp)}</div>
                {notification.action && (
                  <button
                    onClick={(event) => {
                      event.stopPropagation()
                      notification.action?.onClick()
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.625rem',
                      color: '#2563eb',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {notification.action.label}
                  </button>
                )}
                {notification.read && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.625rem', color: '#16a34a' }}>
                    <Check className="w-3 h-3" />
                    lida
                  </span>
                )}
              </div>
            </div>
          ))
          )}
        </div>
      </div>
    </div>
  )
}
